import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MapPin, ShieldCheck, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial } from "@/components/HubEditorial";
import { LocalLinks } from "@/components/LocalLinks";
import { piteaSections } from "@/data/lokala-djup";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-pitea";

const FAQ = [
  {
    q: "Finns det husbilshandlare i Piteå?",
    a: "Utbudet i Piteå är begränsat. Många Piteåbor kör till oss i Kalix — ca 145 km norrut på E4:an — för att vi är officiell Rapido-återförsäljare och kan visa både nya och kontrollerade begagnade husbilar samma dag.",
  },
  {
    q: "Hur långt är det från Piteå till er i Kalix?",
    a: "Ca 145 km, ungefär 1 timme 45 minuter på E4:an norrut via Luleå. Ring innan du åker så står bilarna du är intresserad av framme när du kommer.",
  },
  {
    q: "Kan ni leverera husbilen till Piteå?",
    a: "Ja, vi ordnar transport till Piteå mot en fast avgift. Vi räknar ihop total kostnad tillsammans med köpet så det inte kommer några överraskningar.",
  },
  {
    q: "Servar och fukttestar ni husbilar från Piteå?",
    a: "Ja. Vi tar in husbilar från hela norra Norrbotten för fukttest, vinterservice, gasol- och värmesystem, elarbeten och chassiservice. Boka i god tid inför säsongen.",
  },
  {
    q: "Har ni finansiering för Piteå-kunder?",
    a: "Ja, vi hjälper till med finansiering via etablerade finansbolag oavsett var i Norrbotten du bor. Kontantinsats från 20 % är normalt men går att förhandla.",
  },
];

export const Route = createFileRoute("/husbil-pitea")({
  head: () => ({
    meta: [
      { title: "Husbil Piteå – AK Husbil & Fritid i Kalix" },
      {
        name: "description",
        content:
          "Söker du husbil i Piteå? AK Husbil & Fritid i Kalix — officiell Rapido-återförsäljare, familjeägda sedan 2004. Vi levererar till Piteå. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "husbil Piteå, husbilar Piteå, köpa husbil Piteå, husvagn Piteå, husbilshandlare Piteå, begagnad husbil Piteå" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Husbil Piteå – AK Husbil & Fritid" },
      { property: "og:description", content: "Familjeägd husbilshandlare för Piteå. Officiell Rapido-återförsäljare, kontrollerade fordon, transport ordnas." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: URL_SELF,
          areaServed: ["Piteå", "Luleå", "Boden", "Älvsbyn", "Norrbotten"],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 51",
            addressCountry: "SE",
          },
          telephone: "+46708629743",
        }),
      },
    ],
  }),
  component: HusbilPiteaPage,
});

function HusbilPiteaPage() {
  return (
    <>
      <PageHeader
        label="Husbil · Piteå · Norrbotten"
        title={<>Husbil för dig i <span className="font-italic-display text-accent">Piteå</span>.</>}
        sub="AK Husbil & Fritid ligger i Kalix — 145 km norrut på E4:an. Officiell Rapido-återförsäljare, familjeägda sedan 2004. Många Piteåbor väljer oss för utbudet och den personliga servicen."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/fordon" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Se husbilar i lager <ArrowRight size={14} />
          </Link>
          <Link to="/rapido" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Rapido-modeller
          </Link>
        </div>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Från Piteå till oss</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">145 km. <span className="font-italic-display text-accent">Värt resan.</span></h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Vi ligger på E4:an norr om Luleå. Piteåbor väljer oss för att vi är officiell Rapido-återförsäljare, för att våra begagnade fordon alltid är fukttestade och för att vi svarar när telefonen ringer — även i juli.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Officiell Rapido-återförsäljare för norra Sverige",
                "Kontrollerade begagnade husbilar & husvagnar",
                "Fukttest, gasol/värme, el och chassiservice",
                "Personlig service — samma människor varje gång",
                "Transport till Piteå ordnas mot fast avgift",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <ShieldCheck className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Kör hit</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Stabsvägen 5, 952 51 Kalix. Från Piteå: E4 norrut via Luleå, ca 145 km. Ring först så står de bilar du är intresserad av framme.
            </p>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><MapPin size={14} /> Från Piteå C</dt>
                <dd className="font-medium">145 km · ~1 h 45 min</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><Clock size={14} /> Öppet</dt>
                <dd className="font-medium">Enligt överenskommelse</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Telefon</dt>
                <dd className="font-medium">070-862 97 43</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">E-post</dt>
                <dd className="font-medium">info@akhusbilfritid.se</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Husbil <span className="font-italic-display text-accent">Piteå</span> — vad du behöver veta.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="pt-16 md:pt-20">
        <HubEditorial eyebrow="Lokal fördjupning" sections={piteaSections} />
      </div>

      <LocalLinks
        keyId="husbil-pitea"
        text="husbil piteå norrbotten kust korrosion fukttest vinterförvaring ställplatser driftkostnad finansiering"
        currentHref="/husbil-pitea"
      />

      <section className="container-edge py-16 md:py-24">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Kör upp från Piteå — eller ring först.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Vi bokar in dig, säkerställer att modellerna du är intresserad av står framme och lägger tiden på just din användning.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              <Phone size={16} /> 070-862 97 43
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <MapPin size={16} /> Boka besök i Kalix
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
